// Module singleton — GameWrapper writes, screens read.
//
// MIN_CURTAIN_MS is the floor GameWrapper holds the curtain for. It lives here
// rather than inside GameWrapper because a screen whose entrance is supposed to
// look the same around the table has to know when the room's curtain lifts —
// see WineRatingReveal, which derives its shared reveal instant from it.
// Tracks whether the transition curtain is visible OR still exiting.
// Only flips to false in AnimatePresence onExitComplete (after the exit
// animation fully completes), so callers can safely skip their own entrance
// when the curtain owns the reveal.
export const MIN_CURTAIN_MS = 3500;

const state = { visible: false };
const listeners = new Set();
export const setCurtainVisible = (v) => {
  if (state.visible === v) return;
  state.visible = v;
  listeners.forEach((fn) => fn(v));
};
export const isCurtainVisible = () => state.visible;
// Subscribe to visibility changes (useSyncExternalStore-compatible).
// Returns an unsubscribe fn.
export const subscribeCurtain = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * When a screen should start its entrance, so the room starts it together AND
 * the player actually sees it.
 *
 * Three answers, because there are three different reasons to wait:
 *
 *  'await-curtain' — the curtain is on screen. It counts as visible until
 *    `onExitComplete`, so this covers the 440ms while it is exiting, and that
 *    window is the trap: `game-route-visible` fires at the START of the exit, so
 *    a screen that starts on it plays its entrance behind a curtain still on top
 *    of it. That is the 2026-07-14 QA regression — the heading and group card
 *    were already settled by the time the player could see them. Nothing starts
 *    until the curtain is gone. Caller re-asks on `curtain-exited`.
 *  'wait'          — nothing is covering the screen and the room's instant is
 *    still ahead. Hold for `waitMs`, then play the animation IN FULL. Waiting is
 *    what keeps it whole; seeking or truncating would not.
 *  'run'           — start now, also in full. A late screen plays late rather
 *    than skipping a beat the table has finished watching.
 *
 * The wait is capped because an uncovered screen holding its cards at opacity 0
 * is just a blank screen: sync is worth having only while the player cannot tell
 * it is happening.
 *
 * @param {object}  o
 * @param {number}  o.stepEnteredAtMs  when THIS device entered the step
 * @param {number}  o.nowMs
 * @param {boolean} o.curtainUp        is the curtain on screen or still exiting
 * @param {number}  o.startOffsetMs    the room's reveal instant, after step entry
 * @param {number}  o.uncoveredCapMs   longest wait allowed with nothing on top
 * @returns {{action: 'await-curtain'|'wait'|'run', waitMs: number}}
 */
export function revealStartPlan({
  stepEnteredAtMs,
  nowMs,
  curtainUp,
  startOffsetMs,
  uncoveredCapMs,
}) {
  if (curtainUp) return { action: 'await-curtain', waitMs: 0 };
  if (!Number.isFinite(stepEnteredAtMs) || !Number.isFinite(nowMs)) {
    return { action: 'run', waitMs: 0 };
  }
  const remaining = stepEnteredAtMs + startOffsetMs - nowMs;
  if (!(remaining > 0)) return { action: 'run', waitMs: 0 };
  return { action: 'wait', waitMs: Math.min(remaining, uncoveredCapMs) };
}
